import type { ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { styled } from '@classify/react-core-components/styles';
import Box from '@classify/react-core-components/Box';
import Typography from '@classify/react-core-components/Typography';
import Button, { Props as ButtonProps } from '@classify/react-core-components/Button';
import HighlightedBulletPoint from '@classify/react-marketing-components/HighlightedBulletPoint';
import TagPromotion from '../TagPromotion';

export type MediaFormats = 'square' | 'video';

export interface Props {
  tagProps?: { label?: string };
  title: string;
  subheading?: string;
  bullets?: string[];
  onClose?: () => void;
  isOpen?: boolean;
  firstButtonProps: ButtonProps;
  secondButtonProps?: Omit<ButtonProps, 'variant'>;
  media?: ReactNode;
  mediaFormat?: MediaFormats;
  translations: {
    closeButtonLabel: string;
  };
}

const Wrapper = styled('div')(({ theme: { spacing, breakpoints } }) => ({
  gap: spacing(2),
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  [breakpoints.up('sm')]: {
    gap: spacing(5),
  },
  [breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const Content = styled('div', {
  shouldForwardProp: (prop) => !['hasMedia'].includes(prop as string),
})<{ hasMedia?: boolean }>(({ theme: { spacing, breakpoints }, hasMedia }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: spacing(2),
  paddingRight: spacing(2),
  [breakpoints.up('sm')]: {
    paddingLeft: spacing(4),
    paddingRight: spacing(4),
  },
  [breakpoints.up('md')]: {
    minHeight: 368,

    .../* istanbul ignore next */ (hasMedia ? { paddingRight: spacing(0) } : {}),
  },
}));

const CloseButton = styled(IconButton)(({ theme: { breakpoints, spacing } }) => ({
  position: 'absolute',
  right: spacing(3),
  top: 0,
  padding: spacing(0.5),
  margin: spacing(2, 0, 2, -0.5),
  [breakpoints.up('md')]: {
    position: 'relative',
    right: 0,
  },
}));

const ContentInner = styled('div', {
  shouldForwardProp: (prop) => !['hasMedia'].includes(prop as string),
})<{ hasMedia?: boolean }>(({ theme: { breakpoints, spacing }, hasMedia }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingBottom: /* istanbul ignore next */ hasMedia ? spacing(0) : spacing(3),
  paddingTop: spacing(3),
  [breakpoints.up('md')]: {
    flexGrow: 1,
    paddingTop: 0,
    paddingBottom: spacing(4),
  },
}));

const BulletList = styled('ul')(({ theme: { spacing } }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  margin: spacing(2, 0),
  padding: 0,
}));

const ActionsContainer = styled('div')(({ theme: { breakpoints, spacing } }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: spacing(1),
  paddingTop: spacing(1),
  [breakpoints.up('md')]: {
    gap: spacing(3),
  },
}));

const MediaWrapper = styled('div', {
  shouldForwardProp: (prop) => !['format'].includes(prop as string),
})<{ format: MediaFormats }>(({ theme: { breakpoints, spacing }, format }) => ({
  ...(format === 'square' && {
    alignSelf: 'flex-end',
    justifySelf: 'center',
    aspectRatio: '16 / 9',
    [breakpoints.up('md')]: {
      marginRight: spacing(2),
    },
    '& > *:first-of-type': {
      display: 'block',
    },
    '&, & > *:first-of-type': {
      width: 296,
      [breakpoints.up('md')]: {
        width: 338,
      },
    },
  }),
  ...(format === 'video' && {
    width: '100%',
    [breakpoints.up('md')]: {
      alignSelf: 'center',
      marginRight: spacing(3),
      width: 480,
    },
    '& > iframe:first-of-type, & > video:first-of-type': {
      display: 'block',
      aspectRatio: '16 / 9',
      border: 0,
      objectFit: 'contain',
      width: '100%',
    },
  }),
}));

const NewFeaturePromotion = ({
  tagProps,
  title,
  subheading,
  bullets,
  onClose,
  isOpen,
  firstButtonProps,
  secondButtonProps,
  media,
  mediaFormat = 'square',
  translations = {
    closeButtonLabel: 'Close',
  },
}: Props) => {
  const hasMedia = Boolean(media);
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{ sx: { maxWidth: 1168, minHeight: { md: 368 } } }}
    >
      <Wrapper>
        <Content hasMedia={hasMedia}>
          <div>
            <CloseButton
              aria-label={translations.closeButtonLabel}
              size="large"
              color="inherit"
              onClick={onClose}
            >
              <CloseIcon />
            </CloseButton>
          </div>
          <ContentInner hasMedia={hasMedia}>
            {tagProps?.label && <TagPromotion size="small">{tagProps.label}</TagPromotion>}
            <Typography component="h1" variant="h3">
              {title}
            </Typography>
            {subheading && (
              <Typography component="h2" variant="subheading">
                {subheading}
              </Typography>
            )}
            <BulletList>
              {bullets?.map((bullet) => (
                <HighlightedBulletPoint key={bullet}>{bullet}</HighlightedBulletPoint>
              ))}
            </BulletList>
            <ActionsContainer>
              {firstButtonProps && (
                <Button {...firstButtonProps}>{firstButtonProps.children}</Button>
              )}
              {secondButtonProps && (
                <Button variant="tertiary" {...secondButtonProps}>
                  {secondButtonProps.children}
                </Button>
              )}
            </ActionsContainer>
          </ContentInner>
        </Content>
        {hasMedia && (
          <Box sx={{ display: 'grid' }}>
            <MediaWrapper format={mediaFormat}>{media}</MediaWrapper>
          </Box>
        )}
      </Wrapper>
    </Dialog>
  );
};

export default NewFeaturePromotion;
